import * as React from "react";
import { useHistory, useLocation, NavLink } from "react-router-dom";
import {
  AppBar,
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Toolbar,
  Typography,
  Fade,
  Fab,
  Avatar,
  Paper,
  Breadcrumbs,
  useScrollTrigger,
  useMediaQuery,
  Popover,
  MenuItem,
  Button
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CommonFooter from "./CommonFooter";
import HelpSupportIcon from "./HelpSupportIcon";
import {
  getStudentNavbarSelected,
  getStudentBreadcrumbs,
  getUserInfo,
  isJwtTokenValid
  // setProfileComplete,
} from "utils/helpers";
import { clarityEvent } from "utils/hooks/useClarity";
import StudentNotifications from "./StudentNotificationMenu";
import { colors } from "../../styles/colors";
import theme from "styles/theme";
import images from "assets/images/images";
import GridViewIcon from "@mui/icons-material/GridView";
import { useLazyQuery, useQuery } from "@apollo/client";
import { GET_UPSKILLING_URL } from "app/graphql/users";
import { useSnackbar } from "notistack";
import {
  GET_COLLEGE_LOGO,
  GET_PROFILE_PHOTO,
  GET_USER_ASSESSMENTS
} from "app/graphql/StudentProfile";
import { getMIMETypeOfFile } from "utils/constants";
import { Assessment } from "@mui/icons-material";
import LogoutModal from "./LogoutModal";
import MobileNavigationDrawer from "./StudentMobileNavigationDrawer";
import { GET_ASSESSMENT_PURCHASE_STATUS } from "app/graphql/StudentMESSection";

interface Props {
  window?: () => Window;
  children?: React.ReactElement;
  mainComponent: React.ReactNode;
  clearNotifications?: boolean;
  properties?: any;
}

const drawerWidth = 240;

function ScrollTop(props: Props) {
  const { children, window } = props;
  const trigger = useScrollTrigger({
    target: window ? window() : undefined,
    disableHysteresis: true,
    threshold: 100
  });

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    const anchor = (
      (event.target as HTMLDivElement).ownerDocument || document
    ).querySelector("#back-to-top-anchor");

    if (anchor) {
      anchor.scrollIntoView({
        block: "center"
      });
    }
  };

  return (
    <Fade in={trigger}>
      <Box
        onClick={handleClick}
        role="presentation"
        sx={{ position: "fixed", bottom: 16, right: 16 }}
      >
        {children}
      </Box>
    </Fade>
  );
}

const StudentNavBar = (props: Props) => {
  const { mainComponent, clearNotifications, properties } = props;
  const [profilePhoto, setProfilePhoto] = React.useState<any>(null);
  const history = useHistory();
  const location = useLocation();
  const { first_name } = getUserInfo();
  const { enqueueSnackbar } = useSnackbar();

  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [breadcrumbs, setBreadcrumbs] = React.useState<
    { id: number; name: string; path: string; color: string }[]
  >([]);

  const [assessmentPurchased, setAssessmentPurchased] = React.useState(false);
  
  const { data: purchaseStatusData  } = useQuery(GET_ASSESSMENT_PURCHASE_STATUS, {
    onCompleted: (data) => {
      const { get_assessment_bundle_purchase_status } = data;
      const { status, is_assessment_bundle_purchased } =
        get_assessment_bundle_purchase_status;
      if (status && is_assessment_bundle_purchased) {
        setAssessmentPurchased(true);
      }
    },
    nextFetchPolicy: "network-only",
    fetchPolicy: "network-only"
  });

  const navItems = [
    {
      id: 0,
      name: "Dashboard",
      path: "/student-dashboard",
      image1: images.home_icon_blue,
      image2: images.home_icon_grey
    },
    {
      id: 1,
      name: "Jobs / Campus Drives",
      path: "/student/job-campus-drives",
      image1: images.suitcase_icon_blue,
      image2: images.suitcase_icon_grey
    },
    {
      id: 2,
      name: "Upskilling",
      path: "/student-upskilling",
      image1: images.upskilling_icon_blue,
      image2: images.upskilling_icon_grey
    },
    ...(assessmentPurchased
      ? [
          {
            id: 3,
            name: "Ma Foi Score",
            path: "/student-mafoi-employability-score",
            image1: images.score_icon_blue,
            image2: images.score_icon_grey
          }
        ]
      : []),
    // {
    //   id: 4,
    //   name: "My Application",
    //   path: "/student-myapplication",
    //   image1: images.application_icon_blue,
    //   image2: images.application_icon_grey,
    // },
    {
      id: 4,
      name: "Self-Care",
      path: "/student-selfcare",
      image1: images.selfcare_blue,
      image2: images.selfcare_grey
    }
  ];

  const [get_upskilling_url] = useLazyQuery(GET_UPSKILLING_URL, {
    variables: {},
    onCompleted: (data) => {
      const { get_upskilling_url } = data;
      const { status, loginurl, error } = get_upskilling_url;
      if (status) {
        /**
         * Opens a new browser window or tab with the specified URL and focuses on it.
         *
         * This function is used to open a new window or tab with the specified URL, which is typically used for authentication or other external workflows. The `?.` operator is used to safely call the `focus()` method, which brings the newly opened window or tab into focus.
         */
        window.open(loginurl, "_blank")?.focus();
      } else {
        enqueueSnackbar(error, { variant: "error" });
      }
    },
    fetchPolicy: "network-only",
    nextFetchPolicy: "network-only"
  });

  const { data: campus_logo } = useQuery(GET_COLLEGE_LOGO, {
    variables: {},
    fetchPolicy: "cache-first",
    nextFetchPolicy: "cache-first"
  });

  const matchesXs = useMediaQuery(theme.breakpoints.only("xs"));
  const matchesMd = useMediaQuery(theme.breakpoints.down("md"));
  const { is_assesement_active } = isJwtTokenValid();

  const [shouldFetchData, setShouldFetchData] = React.useState(false);
  const [triggerEffect, setTriggerEffect] = React.useState(false);
  const { data } = useQuery(GET_USER_ASSESSMENTS, { skip: !shouldFetchData });

  React.useEffect(() => {
    if (!!data && is_assesement_active) {
      openWindow();
    }
  }, [data, triggerEffect]);

  const openWindow = () => {
    data?.get_user_assessments?.user_assessments.forEach((assessment) => {
      window.open(assessment)?.focus();
    });
  };

  React.useEffect(() => {
    setBreadcrumbs(getStudentBreadcrumbs(location));
  }, [location]);

  const [logoutModal, setLogoutModal] = React.useState(false);

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(
    null
  );

  const handleProfileIconClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  const {
    data: uploadedProfilePhoto,
    loading: gettingPhoto,
    refetch: getPhoto
  } = useQuery(GET_PROFILE_PHOTO, {
    variables: {},
    fetchPolicy: "cache-first",
    nextFetchPolicy: "cache-first"
  });

  React.useEffect(() => {
    if (uploadedProfilePhoto && uploadedProfilePhoto.get_profile_picture) {
      setProfilePhoto(uploadedProfilePhoto.get_profile_picture);
    } else {
      setProfilePhoto(null);
    }
  }, [uploadedProfilePhoto]);

  return (
    <>
      <Box
        sx={{ 
          display: "flex", 
          flexDirection: "column", 
          minHeight: "100vh",
          mb: location?.pathname?.includes("/student-resume-builder/") ? 0 : 5
        }}
      >
        <AppBar
          component="nav"
          elevation={0}
          color="secondary"
          sx={{
            mt: -1,
            borderBottom: `1px solid ${colors.lightGreyBorder}`,
            boxShadow: `0 2px 4px rgba(0, 0, 0, 0.1)`
          }}
          aria-label="Student navigation"
        >
          <Toolbar sx={{ px: { xs: 0, md: 2 } }}>
            <Box display="flex">
              <Typography
                variant="h6"
                component="div"
                sx={{ display: { xs: "none", md: "block" } }}
              >
                <img
                  src={images.prosculpt_logo_colored}
                  height={45}
                  alt="logo"
                />
              </Typography>
              {campus_logo &&
                campus_logo?.campus_logo &&
                campus_logo?.campus_logo?.document && (
                  <Typography
                    variant="h6"
                    component="div"
                    sx={{ display: { xs: "none", md: "block" }, ml: 5 }}
                  >
                    {/*
                     * Generates the source URL for an image based on the MIME type and base64-encoded data of a campus logo.
                     * @param {string} document_type - The MIME type of the campus logo file.
                     * @param {string} document - The base64-encoded data of the campus logo file.
                     * @returns {string} The source URL for the campus logo image.
                     */}
                    <img
                      src={`data:image/${getMIMETypeOfFile(
                        campus_logo.campus_logo?.document_type
                      )};base64,${campus_logo.campus_logo?.document}`}
                      height={45}
                      alt="logo"
                    />
                  </Typography>
                )}
            </Box>
            <Box
              sx={{
                width: "100%",
                px: { xs: 2, sm: 0 },
                py: { xs: 0.75, sm: 0 },
                display: { xs: "flex", md: "none" },
                borderBottom: `1px solid ${colors.lightGreyBorder}`,
                boxShadow: { xs: "0px 2px 4px rgba(0, 0, 0, 0.1)", sm: "none" },
                minHeight: {xs: "64px", md: "auto"}
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flexGrow: 1,
                  ml: { lg: 10, md: 5, sm: 3 }
                }}
              >
                <img
                  src={images.prosculpt_logo_colored}
                  style={{ width: matchesXs ? "120px" : "110px" }}
                />
              </Box>
              {location?.pathname?.includes("/student-resume-builder/") ? (
                <IconButton
                  color="primary"
                  edge="start"
                  onClick={() => setMobileOpen(true)}
                  sx={{ ml: 4, display: { sm: "flex", md: "none" } }}
                  aria-label="Menu"
                >
                  <GridViewIcon fontSize="large" />
                </IconButton>
              ) : (
                <Box sx={{ display: "flex", alignItems: "center", ml: 4 }}>
                  <StudentNotifications clearNotifications={clearNotifications} />
                  <IconButton
                    color="primary"
                    edge="start"
                    onClick={() => setMobileOpen(true)}
                    sx={{ display: { sm: "flex", md: "none" } }}
                    aria-label="Menu"
                  >
                    <MenuIcon fontSize="large" />
                  </IconButton>
                </Box>
              )}
              <Box component="nav">
                <MobileNavigationDrawer
                  mobileOpen={mobileOpen}
                  setMobileOpen={setMobileOpen}
                />
              </Box>
            </Box>
            <Box
              sx={{
                display: { xs: "none", sm: "none", md: "flex", lg: "flex" },
                justifyContent: "center",
                flexGrow: 1.
              }}
            >
              {navItems &&
                navItems?.map((item, index) => (
                  <Box
                    key={item?.id}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                       borderBottom: getStudentNavbarSelected(location, item)
                          ? 2
                          : 0,
                        borderColor: colors.primaryBlue
                    }}
                  >
                    <img
                      src={
                        getStudentNavbarSelected(location, item)
                          ? item.image1
                          : item.image2
                      }
                      height={20}
                      alt="My SVG"
                      style={{ fill: colors.fontPrimary, marginTop: "16px" }}
                    />
                    <Typography
                      key={item.id}
                      color={
                        getStudentNavbarSelected(location, item)
                          ? colors.primaryBlue
                          : `${colors.fontGrey}`
                      }
                      sx={{
                        cursor: "pointer",
                        mt: 3.5,
                        ml: 1,
                        mr: 2.5,
                        pb: 1.5,
                        fontSize: {
                          xs: "16px",
                          md: "12px",
                          lg: "16px"
                        }
                      }}
                      onClick={() => {
                        history.push(item.path);
                      }}
                      role="button"
                      aria-label={item.name === "Jobs / Campus Drives" ? "Jobs Drives" : item.name === "Self-Care" ? "Self Care" : item.name}
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          history.push(item.path);
                        }
                      }}
                    >
                      {item.name}
                    </Typography>
                  </Box>
                ))}
            </Box>
            <Box
              alignItems={"center"}
              color={colors.fontGrey}
              sx={{ mt: 1, display: { xs: "none", md: "flex" } }}
            >
              <StudentNotifications clearNotifications={clearNotifications} />
              <Button 
                onClick={handleProfileIconClick} 
                aria-label="User profile"
                aria-haspopup="true"
                aria-expanded={open}
                aria-controls="profile-menu"
              >
                {profilePhoto?.photo_url ? (
                  <Avatar sx={{ height: 40, width: 40 }}>
                    <img
                      // src={`data:image/${getMIMETypeOfFile(
                      //   profilePhoto.file_type
                      // )};base64,${profilePhoto.file}`}
                      src={`${profilePhoto?.photo_url}`}
                      alt="logo"
                      height={40}
                      width={40}
                    />
                  </Avatar>
                  ) : ( 
                  <Avatar
                    sx={{
                      cursor: "pointer",
                      color: colors.secondary,
                      backgroundColor: colors.primary,
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center"
                    }}
                  >
                    {first_name?.length > 0 ? first_name[0]?.toUpperCase() : ""}
                  </Avatar>
                )}
              </Button>
              <Popover
                id="profile-menu"
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left"
                }}
                role="menu"
                aria-label="Profile menu"
              >
                <MenuItem
                  sx={{
                    color: colors.lightGreyBorder,
                    mt: 1,
                    mb: -0.5
                  }}
                  onClick={() => {
                    history.push("/student-profile");
                  }}
                  role="menuitem"
                  aria-label="Profile"
                >
                  <img src={images.profile_icon_grey} width={17} />
                  <Typography fontSize={16} color={colors.fontPrimary} ml={2}>
                    Profile
                  </Typography>
                </MenuItem>
                <Divider />
                <MenuItem
                  sx={{
                    color: colors.lightGreyBorder,
                    mt: -0.5,
                    mb: -0.5
                  }}
                  onClick={() => {
                    const userInfo = getUserInfo();
                    if (userInfo.id) {
                      clarityEvent("navbar_resume_clicked", {
                        userId: userInfo.id
                      });
                    }
                    window.open('/student-resume-builder/1', '_blank');
                    handleClose();
                  }}
                  role="menuitem"
                  aria-label="Resume"
                >
                  <img src={images.student_resume_icon} width={17} />
                  <Typography fontSize={16} color={colors.fontPrimary} ml={2}>
                    Resume
                  </Typography>
                </MenuItem>
                <Divider sx={{ my: 0.5 }} />
                <MenuItem
                  sx={{
                    color: colors.lightGreyBorder,
                    mt: -0.5,
                    mb: -0.5
                  }}
                  onClick={() => {
                    history.push("/help-and-support");
                    handleClose();
                  }}
                  role="menuitem"
                  aria-label="Help and Support"
                >
                  <HelpSupportIcon size={17} />
                  <Typography fontSize={16} color={colors.fontPrimary} ml={2}>
                    Help & Support
                  </Typography>
                </MenuItem>
                <Divider sx={{ my: 0.5 }} />
                <MenuItem
                  sx={{
                    color: colors.lightGreyBorder,
                    mt: -0.5,
                    mb: 1
                  }}
                  onClick={() => {
                    setLogoutModal(true);
                    handleClose();
                  }}
                  role="menuitem"
                  aria-label="Log out"
                >
                  <img src={images.logout_icon} width={17} />
                  <Typography fontSize={16} color={colors.fontPrimary} ml={2}>
                    Logout
                  </Typography>
                </MenuItem>
                {/* <Divider
            sx={{
              my: 0,
              height: "5px",
              background: colors.primaryBlue
            }}
          /> */}
              </Popover>
              <LogoutModal
                open={logoutModal}
                setOpen={setLogoutModal}
                width={"400px"}
              />
            </Box>
          </Toolbar>
        </AppBar>
        <Box component="main" sx={{ 
          width: "100%", 
          flexGrow: { 
            xs: 1, // Mobile: unchanged
            md: location?.pathname?.includes("/student-resume-builder/") ? 0 : 1 // Desktop/tablet: fix
          }
        }}>
          <Toolbar id="back-to-top-anchor" sx={{ minHeight: "56px !important" }} />
          {/* <Paper
            elevation={1}
            sx={{ p: 2, pl: matchesMd ? 5 : 15, borderRadius: 0 }}
          >
            <Breadcrumbs separator={">"} aria-label="breadcrumb">
              {breadcrumbs.map((crumb) => (
                <NavLink
                  key={crumb.id}
                  role="button"
                  to={crumb.path}
                  style={{
                    textDecoration: "none",
                    color: crumb.color,
                    fontSize: "16px"
                  }}
                >
                  {crumb.name}
                </NavLink>
              ))}
            </Breadcrumbs>
          </Paper> */}
          <Box
            sx={{
              backgroundColor: colors.lightBlueBackground,
              pt: {
                xs: properties?.py || properties?.py === 0 ? properties?.py : 2, // Mobile: unchanged
                md: location?.pathname?.includes("/student-resume-builder/") ? 0 : 
                    (properties?.py || properties?.py === 0 ? properties?.py : 3) // Desktop/tablet: conditional
              },
              px:
                properties?.px || properties?.px === 0
                  ? properties?.px
                  : matchesMd
                  ? 3
                  : 8,
            }}
          >
            {mainComponent}
          </Box>
        </Box>
        {!location.pathname.includes("/student-resume-builder") && (
          <CommonFooter />
        )}
      </Box>

      <ScrollTop {...props}>
        <Fab color="primary" size="small" aria-label="Back to top">
          <KeyboardArrowUpIcon />
        </Fab>
      </ScrollTop>
    </>
  );
};

export default StudentNavBar;
